import { STUDY_CARD_DATA_TABLES } from "#study-card-data-tables";
import {
  subjectStudyCardDataListQueryFn,
  useGetSubjectStudyCardDataListQueryKey,
} from "#use-subject-study-card-data-list-query";
import { cardIdentityKey } from "@-/dev.improve.haniverse.lib.cards.card-content";
import type { DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

export function useGetReviewCardFsrsResolutionQueryKey() {
  const getSubjectStudyCardDataListQueryKey = useGetSubjectStudyCardDataListQueryKey();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(STUDY_CARD_DATA_TABLES));

  return (grapheme: string, drillType: DrillType) =>
    withTableChangeVersion([
      "reviewCardFsrsResolution",
      grapheme,
      drillType,
      { getSubjectStudyCardDataListQueryKey },
    ] as const);
}

export const [useReviewCardFsrsResolutionQueryOptions, reviewCardFsrsResolutionQueryFn] =
  createUseQueryOptions(
    useGetReviewCardFsrsResolutionQueryKey,
    async ({
      client,
      queryKey: [_, grapheme, drillType, { getSubjectStudyCardDataListQueryKey }],
    }) => {
      // Only THIS subject's cards — the resolution below can only ever match a card whose
      // subject is the route's grapheme, so the whole-deck list it used to load (one content
      // lookup per in-study card) was dead weight that scaled with the deck. Fetched through the
      // registered subject key so it remains the canonical child cache entry.
      const studyCardDataList = await client.fetchQuery({
        queryFn: subjectStudyCardDataListQueryFn,
        queryKey: getSubjectStudyCardDataListQueryKey(grapheme),
      });

      // A route names a card by its identity, the same key every study card carries: a
      // pronunciation by its `pinyin` reading, never by a content-DB id or a deck position.
      const identityKey = cardIdentityKey({ drillType, subjectText: grapheme });
      return studyCardDataList.find((data) => data.identityKey === identityKey) ?? null;
    },
  );
